import React, { useState, type CSSProperties } from "react";
import type { ButtonKey } from "../../constants/gamepad";
import { StaticImage } from "./StaticImage";

interface ButtonProps {
  buttonKey: ButtonKey;
  iconConfig: {
    src1x: string;
    src2x: string;
    src3x: string;
  };
  onTouchStart?: (e: React.TouchEvent, buttonKey: ButtonKey) => void;
  onTouchEnd?: (e: React.TouchEvent, buttonKey: ButtonKey) => void;
  onMouseDown?: (e: React.MouseEvent, buttonKey: ButtonKey) => void;
  onMouseUp?: (e: React.MouseEvent, buttonKey: ButtonKey) => void;
  onClick?: () => void;
  style?: CSSProperties;
  className?: string;
}

export const Button: React.FC<ButtonProps> = ({
  buttonKey,
  iconConfig,
  onTouchStart,
  onTouchEnd,
  onMouseDown,
  onMouseUp,
  onClick,
  style = {},
  className,
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsPressed(true);
    onTouchStart?.(e, buttonKey);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    setIsPressed(false);
    onTouchEnd?.(e, buttonKey);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsPressed(true);
    onMouseDown?.(e, buttonKey);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    setIsPressed(false);
    onMouseUp?.(e, buttonKey);
  };

  return (
    <button
      className={className}
      style={{
        ...baseStyles.button,
        ...style,
        ...(isPressed && baseStyles.pressed),
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={onClick}
      onContextMenu={(e) => e.preventDefault()}
    >
      <StaticImage
        src={iconConfig.src1x}
        src2x={iconConfig.src2x}
        src3x={iconConfig.src3x}
      />
    </button>
  );
};

const baseStyles: { [key: string]: CSSProperties } = {
  button: {
    background: "transparent",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
    WebkitUserSelect: "none",
    cursor: "pointer",
    transition: "all 0.1s",
    touchAction: "none",
    WebkitTapHighlightColor: "transparent",
    WebkitTouchCallout: "none",
    pointerEvents: "auto",
  },
  pressed: {
    opacity: 0.65,
  },
};
